/**
 * DeleteUserDialog
 *
 * Guarded admin deletion dialog. Workflow:
 *   1. Load preview inventory and blocking checks.
 *   2. Show aggregate data / provider / billing impact.
 *   3. Require typing "DELETE" and entering a fresh 6-digit TOTP code.
 *   4. Call the admin-delete-user-account-v2 Edge Function (execute op).
 *   5. Show durable per-step progress, partial failures, and retry controls.
 *   6. Emit onComplete when verified completion is recorded.
 *
 * Props:
 *   open            boolean
 *   onOpenChange    (open: boolean) => void
 *   targetUserId    string   — UUID of the user to delete
 *   targetEmail     string   — displayed (not stored) for operator context
 *   onComplete      () => void — called after verified completion
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Loader2, AlertTriangle, ShieldCheck, CheckCircle2, XCircle, RotateCcw } from 'lucide-react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/lib/customSupabaseClient';
import { toast } from 'sonner';

const EDGE_FUNCTION = 'admin-delete-user-account-v2';
const CONFIRM_WORD  = 'DELETE';

/** Call the v2 admin deletion Edge Function. */
async function callDeletionApi(op, body) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${EDGE_FUNCTION}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ op, ...body }),
    },
  );
  const json = await res.json();
  if (!res.ok && !json.partial) {
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
  return json;
}

/** Render a per-step status row. */
function StepRow({ label, status }) {
  if (!status) return null;
  const isDone   = status.status === 'done';
  const isSkipped = status.skipped;
  return (
    <div className="flex items-center justify-between text-sm py-1">
      <span className="text-muted-foreground">{label}</span>
      {isDone ? (
        isSkipped
          ? <Badge variant="secondary" className="text-xs">Skipped</Badge>
          : <CheckCircle2 className="h-4 w-4 text-green-500" />
      ) : (
        <XCircle className="h-4 w-4 text-destructive" />
      )}
    </div>
  );
}

export default function DeleteUserDialog({
  open,
  onOpenChange,
  targetUserId,
  targetEmail,
  onComplete,
}) {
  const { isAal2, hasTotpFactor, mfaFactors, createDeletionMfaToken } = useAuth();

  const [phase, setPhase] = useState('idle'); // idle | loading-preview | preview | confirming | executing | done | error
  const [preview, setPreview] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [executing, setExecuting] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [stepStatuses, setStepStatuses] = useState({});
  const [errors, setErrors] = useState([]);
  const [fatalError, setFatalError] = useState('');

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setPhase('loading-preview');
      setPreview(null);
      setBlocked(null);
      setConfirmText('');
      setTotpCode('');
      setExecuting(false);
      setRequestId(null);
      setStepStatuses({});
      setErrors([]);
      setFatalError('');
    }
  }, [open]);

  // Load preview when dialog opens
  useEffect(() => {
    if (phase !== 'loading-preview' || !targetUserId) return;
    (async () => {
      try {
        const result = await callDeletionApi('preview', { target_user_id: targetUserId });
        setPreview(result);
        setBlocked(result.blocked ? result.error : null);
        setPhase('preview');
      } catch (err) {
        setFatalError(err.message);
        setPhase('error');
      }
    })();
  }, [phase, targetUserId]);

  const handleConfirm = async () => {
    if (confirmText !== CONFIRM_WORD) {
      toast.error(`Type ${CONFIRM_WORD} to confirm`);
      return;
    }
    if (totpCode.length !== 6) {
      toast.error('Enter the 6-digit authenticator code');
      return;
    }
    if (!isAal2 || !hasTotpFactor) {
      toast.error('AAL2 session required — re-authenticate with your authenticator app');
      return;
    }

    setExecuting(true);

    // Register a one-use MFA token
    const factorId = mfaFactors[0]?.id;
    const tokenResult = await createDeletionMfaToken(factorId, totpCode);
    if (tokenResult.error) {
      toast.error('TOTP verification failed', { description: tokenResult.error.message });
      setTotpCode('');
      setExecuting(false);
      return;
    }

    setPhase('executing');
    try {
      const result = await callDeletionApi('execute', {
        target_user_id: targetUserId,
        mfa_token_id: tokenResult.tokenId,
      });

      setRequestId(result.request_id);
      setStepStatuses(result.step_statuses ?? {});
      setErrors(result.errors ?? []);

      if (result.partial) {
        setPhase('done');
        toast.warning('Deletion completed with some failures — review steps below');
      } else if (result.success) {
        setPhase('done');
        toast.success('Account deleted successfully');
        onComplete?.();
      } else {
        setFatalError(result.error ?? 'Deletion failed');
        setPhase('error');
      }
    } catch (err) {
      setFatalError(err.message);
      setPhase('error');
    } finally {
      setExecuting(false);
    }
  };

  const handleRetry = async () => {
    if (!requestId) return;
    setExecuting(true);
    setPhase('executing');
    try {
      const result = await callDeletionApi('retry', { request_id: requestId });
      setStepStatuses(result.step_statuses ?? {});
      setErrors(result.errors ?? []);
      if (result.success) {
        setPhase('done');
        toast.success('All steps completed successfully');
        onComplete?.();
      } else {
        setPhase('done');
        toast.warning('Still some partial failures — check steps');
      }
    } catch (err) {
      setFatalError(err.message);
      setPhase('error');
    } finally {
      setExecuting(false);
    }
  };

  const pollStatus = useCallback(async () => {
    if (!requestId) return;
    try {
      const result = await callDeletionApi('status', { request_id: requestId });
      setStepStatuses(result.request?.step_statuses ?? {});
    } catch (_e) {
      // non-critical
    }
  }, [requestId]);

  // Poll step status while executing
  useEffect(() => {
    if (phase !== 'executing' || !requestId) return;
    const interval = setInterval(pollStatus, 2500);
    return () => clearInterval(interval);
  }, [phase, requestId, pollStatus]);

  const canConfirm = confirmText === CONFIRM_WORD && totpCode.length === 6 && !executing;
  const hasPartialFailure = errors.length > 0;

  const STEP_LABELS = {
    stripe_subscriptions: 'Cancel Stripe subscriptions',
    stripe_customer:      'Delete Stripe customer',
    storage:              'Remove Storage files',
    agent_tables:         'Erase AI / agent data',
    core_rpc:             'Erase database records',
    auth_user:            'Delete Auth account',
    brevo:                'Remove Brevo contact',
    posthog:              'Remove PostHog person',
    residual_check:       'Residual verification',
    confirmation_email:   'Send confirmation email',
    audit_record:         'Write audit record',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete Account
          </DialogTitle>
          <DialogDescription>
            This permanently erases all data across every system. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {/* ── Loading preview ─────────────────────────────────────────────── */}
        {phase === 'loading-preview' && (
          <div className="flex items-center justify-center py-10 gap-3 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading account inventory…</span>
          </div>
        )}

        {/* ── Error ───────────────────────────────────────────────────────── */}
        {phase === 'error' && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{fatalError}</span>
          </div>
        )}

        {/* ── Preview ─────────────────────────────────────────────────────── */}
        {(phase === 'preview' || phase === 'confirming') && preview && (
          <div className="space-y-4">
            {blocked && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{blocked}</span>
              </div>
            )}

            {!blocked && (
              <>
                <div>
                  <p className="text-sm font-medium mb-2">Data that will be erased</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    {Object.entries(preview.inventory ?? {})
                      .filter(([k]) => !['has_stripe_customer', 'has_avatar_storage'].includes(k))
                      .filter(([, v]) => typeof v === 'number')
                      .map(([key, count]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-muted-foreground">{key.replace(/_/g, ' ')}</span>
                          <span className={count > 0 ? 'font-medium' : 'text-muted-foreground'}>
                            {count}
                          </span>
                        </div>
                      ))
                    }
                  </div>
                </div>

                {(preview.active_subscriptions?.length > 0 || preview.has_stripe_customer) && (
                  <>
                    <Separator />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">External services</p>
                      {preview.active_subscriptions?.length > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {preview.active_subscriptions.length} active subscription(s) will be cancelled
                        </Badge>
                      )}
                      {preview.has_stripe_customer && (
                        <p className="text-xs text-muted-foreground">Stripe customer will be deleted</p>
                      )}
                    </div>
                  </>
                )}

                <Separator />

                {/* Confirmation inputs */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="confirm-text">
                      Type <strong className="font-mono">{CONFIRM_WORD}</strong> to confirm
                    </Label>
                    <Input
                      id="confirm-text"
                      placeholder={CONFIRM_WORD}
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                    />
                  </div>

                  {hasTotpFactor ? (
                    <div className="space-y-2">
                      <Label htmlFor="totp-code" className="flex items-center gap-1.5">
                        <ShieldCheck className="h-4 w-4" />
                        Authenticator code
                      </Label>
                      <Input
                        id="totp-code"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        placeholder="000000"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        autoComplete="one-time-code"
                        className="text-center text-lg tracking-widest"
                      />
                      <p className="text-xs text-muted-foreground">
                        Enter a fresh code from your authenticator app.
                        The code is valid for 2 minutes from the moment you submit.
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>
                        You must enroll an authenticator before you can delete accounts.
                        Visit <strong>/mfa/enroll</strong> to set one up.
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Executing / progress ─────────────────────────────────────────── */}
        {(phase === 'executing' || phase === 'done') && (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              {phase === 'executing' ? 'Deletion in progress…' : 'Deletion result'}
            </p>
            <div className="rounded-md border p-3 space-y-0.5 bg-muted/30">
              {Object.entries(STEP_LABELS).map(([key, label]) => (
                <StepRow key={key} label={label} status={stepStatuses[key]} />
              ))}
            </div>

            {errors.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-destructive">Errors</p>
                {errors.map((err, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    <strong>{err.step}:</strong> {err.error}
                  </p>
                ))}
              </div>
            )}

            {phase === 'executing' && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing…
              </div>
            )}

            {phase === 'done' && !hasPartialFailure && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                Account permanently deleted
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          {(phase === 'preview' || phase === 'error') && !blocked && (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={executing}
              >
                Cancel
              </Button>
              {phase === 'preview' && hasTotpFactor && (
                <Button
                  variant="destructive"
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                >
                  {executing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Permanently Delete Account
                </Button>
              )}
            </>
          )}

          {phase === 'preview' && blocked && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}

          {phase === 'done' && hasPartialFailure && requestId && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button variant="destructive" onClick={handleRetry} disabled={executing}>
                {executing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                Retry Failed Steps
              </Button>
            </>
          )}

          {phase === 'done' && !hasPartialFailure && (
            <Button onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
